export type MapProvider = "apple" | "google";

export type MapDestination = {
	useAddress?: boolean | null;
	centerLat?: string | number | null;
	centerLng?: string | number | null;
	addressLineOne?: string | null;
	addressLineTwo?: string | null;
	city?: string | null;
	state?: string | null;
	country?: string | null;
	postalCode?: string | null;
};

const APPLE_MAPS_URL = "http://maps.apple.com/";
const GOOGLE_MAPS_PLACE_URL = "https://www.google.com/maps/place/";

const ADDRESS_FIELDS = [
	"addressLineOne",
	"addressLineTwo",
	"city",
	"state",
	"country",
	"postalCode",
] as const satisfies readonly (keyof MapDestination)[];

const toAddressQuery = (destination: MapDestination): string =>
	ADDRESS_FIELDS.map((field) => destination[field]?.toString().trim())
		.filter(Boolean)
		.join(",");

const toCoordinateQuery = ({ centerLat, centerLng }: MapDestination): string => {
	const lat = centerLat?.toString().trim();
	const lng = centerLng?.toString().trim();

	return lat && lng ? `${lat},${lng}` : "";
};

/**
 * Returns undefined when the place has neither a usable address nor coordinates, so callers can
 * hide the link rather than render one that resolves nowhere.
 */
export const buildMapUrl = ({
	destination,
	provider,
}: {
	destination: MapDestination;
	provider: MapProvider;
}): string | undefined => {
	const address = toAddressQuery(destination);
	const coordinates = toCoordinateQuery(destination);
	const query = destination.useAddress ? address || coordinates : coordinates || address;

	if (!query) {
		return undefined;
	}

	if (provider === "apple") {
		return `${APPLE_MAPS_URL}?daddr=${encodeURIComponent(query)}&dirflg=d`;
	}

	return `${GOOGLE_MAPS_PLACE_URL}${encodeURIComponent(query)}`;
};
